import React, { useCallback, useEffect } from 'react';
import {
  Form, Input, Modal, Row, Select, Switch, Tag, Typography,
} from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  selectChecklists, selectEditState,
  selectInspectionReasonsPageDataRequest, selectSubmitInspectionReasonRequest,
} from '../../../../state/ui/configuration/inspection/inspectionReasonUiReducer';
import { selectInspectionReason } from '../../../../state/domain/inspectionReasons/inspectionReasonReducer';
import {
  cancelEditInspectionReason, requestAddInspectionReason, requestUpdateInspectionReason,
} from '../../../../state/ui/configuration/inspection/inspectionReasonUiActions';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import { sortAlphabeticallyByAttribute } from './config-util';

const { Option } = Select;
const { Text } = Typography;


// FIELDS
/**
 * The field for the description label.  Allows clear, and max length set.  Required plus extra duplicate validation.
 * The rules are injected into the form using the form field decorator.
 */
const DescriptionField = injectIntl(({
  form, reason, intl, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="description" />}>
    { form.getFieldDecorator('reasonDescription', {
      initialValue: reason ? reason.reasonDescription : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (value !== (reason ? reason.reasonDescription : '')
              && checkForDuplicates(reason ? reason.reasonCode : null, value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the compliance label.  Toggle that also controls mandatory flag on checklist.
 * The rules are injected into the form using the form field decorator.
 */
const ComplianceField = injectIntl(({ form, reason }) => (
  <Form.Item label={<FormattedMessage id="complianceInspection" />}>
    { form.getFieldDecorator('compliance', {
      initialValue: reason ? reason.compliance : false,
    })(
      <Switch
        checked={form.getFieldValue('compliance')}
        onChange={(val) => {
          // eslint-disable-next-line no-param-reassign
          form.setFieldsValue({ compliance: val });
          return val;
        }}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the your reference switch.
 * The rules are injected into the form using the form field decorator.
 */
const YourReferenceField = injectIntl(({ form, reason }) => (
  <Form.Item label={<FormattedMessage id="yourReferenceMandatory" />}>
    { form.getFieldDecorator('yourRefMandatoryInd', {
      initialValue: reason ? reason.yourRefMandatoryInd : false,
    })(
      <Switch
        checked={form.getFieldValue('yourRefMandatoryInd')}
        onChange={(val) => {
          // eslint-disable-next-line no-param-reassign
          form.setFieldsValue({ yourRefMandatoryInd: val });
          return val;
        }}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the checklist label.  Select with active checklists.  Required validation if compliance true.
 * The rules are injected into the form using the form field decorator.
 */
const ChecklistField = injectIntl(({
  form, reason, intl, checklists, checklistsLoading, checkListFieldName, extraLabelTranslationKey,
}) => (
    <Form.Item label={<span><FormattedMessage id="checklist" /> - <FormattedMessage id={extraLabelTranslationKey.toString()} /></span>}>
    { form.getFieldDecorator(checkListFieldName, {
      rules: [],
      initialValue: reason && reason.hasOwnProperty(checkListFieldName) ? reason[checkListFieldName] : undefined,
    })(
      <Select
        loading={checklistsLoading}
        placeholder={intl.formatMessage({ id: 'selectOne' })}
        allowClear
        optionLabelProp="label"
        optionFilterProp="label"
        filterOption={
          (input, option) => (
            option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          )}
      >
        {sortAlphabeticallyByAttribute(checklists, 'description', false).map(item => (
          <Option key={item.checkListCode} value={item.checkListCode} label={item.description}>
            <Row type="flex" justify="space-between" align="middle">
              <Text style={{ width: '60%', whiteSpace: 'pre-wrap' }}>{item.description}</Text>
              <Tag color="volcano" style={{ display: item.active ? 'none' : '', height: '22px' }}>
                <FormattedMessage id="notPublished" />
              </Tag>
            </Row>
          </Option>
        ))}
      </Select>,
    )}
  </Form.Item>
));

// MODAL AND FORM
const EditReason = injectIntl(Form.create({ name: 'edit-reason' })(({
  intl, form, visible, setVisible, reason, onCreate, onUpdate, descValidator,
  onCancel, isLoading, checklists, isSubmitting, validationBox, ...rest
}) => {
  const isCreating = !reason;
  const titleId = isCreating ? 'createInspectionReason' : 'updateInspectionReason';
  const buttonId = 'save';

  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) resetFields();
  }, [resetFields, visible]);

  // On form submit.
  const submitForm = useCallback(() => {
    form.validateFields((err, values) => {
      if (!err) {
        const newReason = reason
          ? { ...reason, ...values }
          : { active: false, ...values };
        delete newReason.checkListCodesCombined;
        const submitFunction = isCreating ? onCreate : onUpdate;
        submitFunction(newReason);
      }
    });
  }, [form, reason, isCreating, onCreate, onUpdate]);

  return (
    <Modal
      title={<FormattedMessage id={titleId} />}
      okText={<FormattedMessage id={buttonId} />}
      onOk={submitForm}
      visible={visible}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
        <ViaValidationAlert validationError={validationBox} />
        <DescriptionField form={form} reason={reason} checkForDuplicates={descValidator} />
        <ComplianceField form={form} reason={reason} />
        <YourReferenceField form={form} reason={reason} />
        <ChecklistField form={form} reason={reason} checklists={checklists} checklistsLoading={isLoading} checkListFieldName="beforeDamageCheckListCode" extraLabelTranslationKey="beforeDamage" />
        <ChecklistField form={form} reason={reason} checklists={checklists} checklistsLoading={isLoading} checkListFieldName="afterDamageCheckListCode" extraLabelTranslationKey="afterDamage" />
      </Form>
    </Modal>
  );
}));

// Validate the prop type parameters.
EditReason.propTypes = {
  visible: PropTypes.bool.isRequired,
  reason: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    reasonDescription: PropTypes.string.isRequired,
    beforeDamageCheckListCode: PropTypes.string,
    afterDamageCheckListCode: PropTypes.string,
  }),
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  checklists: PropTypes.arrayOf(PropTypes.shape({
    checkListCode: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  })),
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
};

EditReason.defaultProps = {
  reason: null,
  isLoading: false,
  checklists: [],
  validationBox: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitInspectionReasonRequest(state);
  const pageDataRequest = selectInspectionReasonsPageDataRequest(state);
  const checklists = selectChecklists(state);

  return {
    visible: editState.isEditing,
    reason: editState.reasonCode ? selectInspectionReason(state, editState.reasonCode) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    checklists: checklists || [],
    validationBox: submitRequest ? submitRequest.error : undefined,
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: reason => dispatch(requestAddInspectionReason(reason)),
  onUpdate: reason => dispatch(requestUpdateInspectionReason(reason)),
  onCancel: () => dispatch(cancelEditInspectionReason()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditReason);
